import { Link } from "react-router-dom";
import { ArrowLeft, Coffee, UtensilsCrossed, Waves, Car } from "lucide-react";
import { SiteNav } from "@/components/SiteNav";
import { SiteFooter } from "@/components/SiteFooter";
import { Reveal } from "@/components/Reveal";
import { photoUrl } from "@/lib/api";
import selection from "@/data/photoSelection.json";

const GROUPS = [
  {
    key: "coffee",
    icon: Coffee,
    eyebrow: "Coffee & Breakfast",
    items: [
      { name: "Tiare's", note: "A local favourite for relaxed morning coffee and fresh island breakfasts." },
      { name: "VMV Bakery", note: "Great baked goods and coffee — a quick stop on your way out for the day." },
      { name: "Beluga", note: "Stylish café with excellent coffee and a menu worth lingering over." },
      { name: "Love Cafe", note: "Wholesome, feel-good food in a relaxed setting — perfect for a slow start." },
    ],
    image: selection.espresso,
  },
  {
    key: "dinner",
    icon: UtensilsCrossed,
    eyebrow: "Dinner & Drinks",
    items: [
      { name: "Beach Hut", note: "Relaxed beachside dining with great food and that classic island atmosphere." },
      { name: "Saigon Shack", note: "Fresh, flavourful Vietnamese — a crowd favourite on the island." },
      { name: "Tamarind House", note: "Refined island cooking in a heritage setting — perfect for a special evening." },
      { name: "Falafel Shack", note: "Casual, delicious and great value — a reliable go-to any night of the week." },
      { name: "Vaiana's", note: "Warm Cook Islands hospitality and incredible food in a laid-back local spot." },
    ],
    image: selection.dining,
  },
  {
    key: "beaches",
    icon: Waves,
    eyebrow: "Beaches",
    items: [
      { name: "Muri Beach", note: "Crystal-clear lagoon waters right on our doorstep — bring the paddleboard." },
      { name: "Titikaveka", note: "One of the island's best swimming and snorkelling spots." },
    ],
    image: selection.pool,
  },
];

const TRANSPORT = [
  { heading: "Car Hire", note: "We recommend hiring a car to get the most out of the island. It opens up beaches, restaurants, and hidden spots that are hard to reach otherwise." },
  { heading: "Bus", note: "The bus stop is only 20 metres from the house, with an hourly service running around the entire island. Easy, cheap, and a great way to see local life." },
];

export default function IslandGuidePage() {
  return (
    <div className="relative">
      <SiteNav />

      <section className="pt-32 md:pt-40 pb-2 md:pb-4">
        <div className="container-editorial">
          <Reveal>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--ink))] transition-colors mb-10"
            >
              <ArrowLeft size={14} strokeWidth={1.4} /> Back to Ata House
            </Link>
            <div className="eyebrow-rule">Island Guide</div>
            <h1 className="editorial-heading text-4xl md:text-5xl lg:text-6xl">
              Our favourite <em>places.</em>
            </h1>
            <p className="mt-6 text-[hsl(var(--muted-foreground))] max-w-2xl">
              A small, personal list of the spots we love on Rarotonga. Slow mornings,
              long dinners, and the lagoon in between.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="container-editorial py-20 space-y-24">
        {GROUPS.map((g, gi) => (
          <section key={g.key} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <Reveal className={`lg:col-span-5 ${gi % 2 === 1 ? "lg:order-2" : ""}`}>
              <div className="img-frame aspect-[4/5]">
                <img src={photoUrl(g.image)} alt={g.eyebrow} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </Reveal>

            <Reveal delay={100} className={`lg:col-span-7 ${gi % 2 === 1 ? "lg:order-1" : ""}`}>
              <div className="flex items-center gap-3 mb-6">
                <g.icon size={18} strokeWidth={1.25} className="text-[hsl(var(--sage-deep))]" />
                <div className="tracking-eyebrow text-[10px] text-[hsl(var(--sage-deep))]">
                  {g.eyebrow}
                </div>
              </div>
              <div className="space-y-8">
                {g.items.map((it) => (
                  <article key={it.name} className="border-t border-[hsl(var(--border))] pt-6">
                    <h3 className="font-display text-2xl md:text-3xl">{it.name}</h3>
                    <p className="mt-3 text-[hsl(var(--muted-foreground))] max-w-lg leading-relaxed">
                      {it.note}
                    </p>
                  </article>
                ))}
              </div>
            </Reveal>
          </section>
        ))}

        {/* Transport section */}
        <section>
          <Reveal>
            <div className="flex items-center gap-3 mb-10">
              <Car size={18} strokeWidth={1.25} className="text-[hsl(var(--sage-deep))]" />
              <div className="tracking-eyebrow text-[10px] text-[hsl(var(--sage-deep))]">Transport</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {TRANSPORT.map((t) => (
                <div key={t.heading} className="border-t border-[hsl(var(--border))] pt-6">
                  <h3 className="font-display text-2xl md:text-3xl mb-3">{t.heading}</h3>
                  <p className="text-[hsl(var(--muted-foreground))] leading-relaxed">{t.note}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </section>
      </div>

      <SiteFooter />
    </div>
  );
}
